import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Row, Col, Button, Modal, Form, Input, Tooltip, message, Space } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, BankOutlined } from '@ant-design/icons';
import EditHostelModal from '../components/EditHostelModal';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

const HostelList = () => {
  const [hostels, setHostels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingHostel, setEditingHostel] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  useEffect(() => {
    fetchHostels();
  }, []);

  const fetchHostels = async () => {
    try {
      const response = await api.get('/hostels');
      setHostels(response.data);
    } catch (error) {
      message.error('Failed to load hostels');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (hostel) => {
    Modal.confirm({
      title: `Delete "${hostel.name}"?`,
      content: 'This cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await api.delete(`/hostels/${hostel._id}`);
          message.success('Hostel deleted');
          fetchHostels();
        } catch (error) {
          message.error(error.response?.data?.message || 'Delete failed');
        }
      },
    });
  };

  const handleAddSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        name: values.name,
        address: {
          street: values.street || '',
          city: values.city,
          country: values.country,
          postalCode: values.postalCode || '',
        },
        description: values.description || '',
      };
      await api.post('/hostels', payload);
      message.success('Hostel created');
      form.resetFields();
      setShowAddModal(false);
      fetchHostels();
    } catch (err) {
      if (err.errorFields) return;
      message.error(err.response?.data?.message || 'Create failed');
    }
  };

  if (loading) return null;

  return (
    <>
      <Card
        title={<span style={{ fontSize: 20, fontWeight: 600 }}><BankOutlined /> Hostels</span>}
        extra={
          user?.role === 'owner' && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setShowAddModal(true)}>
              Add Hostel
            </Button>
          )
        }
      >
        <Row gutter={[16, 16]}>
          {hostels.map((hostel) => (
            <Col xs={24} sm={12} lg={8} xl={6} key={hostel._id}>
              <Card
                hoverable
                onClick={() => navigate(`/hostels/${hostel._id}`)}
                style={{ cursor: 'pointer' }}
                actions={
                  user?.role === 'owner'
                    ? [
                        <Tooltip key="edit" title="Edit"><Button type="link" size="small" icon={<EditOutlined />} onClick={(e) => { e.stopPropagation(); setEditingHostel(hostel); }} /></Tooltip>,
                        <Tooltip key="del" title="Delete"><Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={(e) => { e.stopPropagation(); handleDelete(hostel); }} /></Tooltip>,
                      ]
                    : undefined
                }
              >
                <Card.Meta
                  title={hostel.name}
                  description={
                    <>
                      <div>{hostel.address?.street}</div>
                      <div>{hostel.address?.city}, {hostel.address?.country}</div>
                      <div style={{ marginTop: 8 }}>Rooms: {hostel.rooms?.length || 0}</div>
                    </>
                  }
                />
              </Card>
            </Col>
          ))}
        </Row>
      </Card>

      <Modal
        title="Add New Hostel"
        open={showAddModal}
        onCancel={() => { setShowAddModal(false); form.resetFields(); }}
        onOk={handleAddSubmit}
        destroyOnClose
        okText="Create"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Hostel Name" rules={[{ required: true }]}>
            <Input placeholder="Name" />
          </Form.Item>
          <Form.Item name="street" label="Street">
            <Input placeholder="Street" />
          </Form.Item>
          <Form.Item name="city" label="City" rules={[{ required: true }]}>
            <Input placeholder="City" />
          </Form.Item>
          <Form.Item name="country" label="Country" rules={[{ required: true }]}>
            <Input placeholder="Country" />
          </Form.Item>
          <Form.Item name="postalCode" label="Postal Code">
            <Input placeholder="Postal Code" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} placeholder="Description" />
          </Form.Item>
        </Form>
      </Modal>

      {editingHostel && (
        <EditHostelModal
          hostel={editingHostel}
          onClose={() => setEditingHostel(null)}
          onSuccess={() => { setEditingHostel(null); fetchHostels(); }}
        />
      )}
    </>
  );
};

export default HostelList;
